// @ts-nocheck
/**
 * CDR media details — "View Details" link that opens a dialog listing the
 * recordings + voicemails referenced by a CDR media pointer, with inline
 * <audio> players.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { fetchCdrMediaDetails, isMediaReady, MEDIA_NOT_READY_TITLE } from '@/utilities/cdrMedia';
import { canReadRecordings, hasVisibleCdrMedia } from '@/utilities/recordingsAcl';
import { getAgentMasking, maskIfEnabled } from '@/utilities/cliDisplay';
import { getUser } from '@/utilities/setting';
import MediaAudioPlayer from './MediaAudioPlayer';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const formatSize = (bytes: any) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '-';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const formatWhen = (value: any) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
};

const MediaRows = ({ title, rows }: any) => {
  if (!rows.length) return null;
  const agentMask = getAgentMasking(getUser());

  return (
    <div className="mb-4">
      <div className="mb-1.5 text-sm font-semibold">
        {title} ({rows.length})
      </div>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-xs">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-2 py-2 text-left font-medium">ID</th>
              {/* <th className="px-2 py-2 text-left font-medium">File</th> */}
              <th className="px-2 py-2 text-left font-medium">Agent</th>
              <th className="px-2 py-2 text-left font-medium">DID</th>
              <th className="px-2 py-2 text-left font-medium">Size</th>
              <th className="px-2 py-2 text-left font-medium">Created</th>
              <th className="px-2 py-2 text-left font-medium">Audio</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row: any) => (
              <tr key={`${row.kind}-${row.id}`} className="border-t border-border">
                <td className="px-2 py-2">{row.id}</td>
                {/* <td className="px-2 py-2 break-all">
                  {row.file_name || row.path || '-'}
                  {row.type ? <div className="text-muted-foreground">{row.type}</div> : null}
                </td> */}
                <td className="px-2 py-2">{row.agent_name || row.agent_group_name || '-'}</td>
                <td className="px-2 py-2">{maskIfEnabled(agentMask, row.did_number ?? row.did ?? '-')}</td>
                <td className="px-2 py-2">{formatSize(row.size)}</td>
                <td className="px-2 py-2">{formatWhen(row.created_at)}</td>
                <td className="px-2 py-2">
                  <MediaAudioPlayer
                    src={row.audio_url}
                    disabled={!isMediaReady(row)}
                    disabledTitle={MEDIA_NOT_READY_TITLE}
                    className="max-w-[210px]"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const CdrMediaDetails = ({ recordingFile, compact = false }: any) => {
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState<any>({ recordings: [], voicemails: [], errors: [] });

  // No Recordings read ACL → no View Details link at all (voicemails included).
  if (!hasVisibleCdrMedia(recordingFile)) return null;

  const openDetails = async () => {
    setShow(true);
    setLoading(true);
    try {
      const result = await fetchCdrMediaDetails(recordingFile);
      setDetails(canReadRecordings() ? result : { ...result, recordings: [] });
      if (result.errors.length > 0) {
        toast.error(`Could not load some media — ${result.errors.join('; ')}`);
      }
    } catch {
      toast.error('Unable to load call media details');
      setDetails({ recordings: [], voicemails: [], errors: [] });
    } finally {
      setLoading(false);
    }
  };

  const total = details.recordings.length + details.voicemails.length;

  return (
    <>
      <button
        type="button"
        onClick={openDetails}
        title="View recordings / voicemails for this call"
        className={
          'm-1 inline-block cursor-pointer border-0 bg-transparent p-0 text-primary underline hover:text-primary/80 ' +
          (compact ? 'text-xs' : 'text-[13px]')
        }
      >
        View Details
      </button>

      <Dialog open={show} onOpenChange={(next) => !next && setShow(false)}>
        {/* z above report side panels (z-1050) this link is often rendered inside. */}
        <DialogContent className="z-[1100] max-w-3xl" overlayClassName="z-[1100]">
          <DialogHeader>
            <DialogTitle className="text-base">Call Media Details</DialogTitle>
          </DialogHeader>
          <div className="max-h-[70vh] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : total === 0 ? (
              <div className="py-4 text-center text-sm text-muted-foreground">
                No recordings or voicemails available for this call.
              </div>
            ) : (
              <>
                <MediaRows title="Recordings" rows={details.recordings} />
                <MediaRows title="Voicemails" rows={details.voicemails} />
              </>
            )}
          </div>
          <DialogFooter>
            <Button size="sm" onClick={() => setShow(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CdrMediaDetails;
